import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc,
  orderBy,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  deleteField
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { ROLES, STATUSES, PLANS, PLAN_STATUS } from '../constants/roles';
import { db } from '../firebase';
import { migratePendingTeachers, dryRunPendingTeachersMigration } from '../utils/migratePendingTeachers';
import { safeCount } from '../lib/safeCount';
import { normalizeLegacyStudents } from '../utils/migrations';

export default function AdminApproval() {
  const { userDoc } = useAuth();
  
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [message, setMessage] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [newSubscription, setNewSubscription] = useState({ plan: 'monthly', studentLimit: 30 });
  const [migrationLoading, setMigrationLoading] = useState(false);

  useEffect(() => {
    if (userDoc && userDoc.role === ROLES.MASTER) {
      setupPendingUsersListener();
    }
  }, [userDoc]);

  const setupPendingUsersListener = async () => {
    try {
      // Simplified query for all pending users (matches MasterAdminDashboard)
      const pendingUsersQ = query(
        collection(db, "users"),
        where("status", "==", "pending")
      );

      const pendingUsersSnap = await getDocs(pendingUsersQ);
      const allPendingUsers = pendingUsersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Filter for teacher-related pending accounts and sort by creation time
      const finalUsers = allPendingUsers
        .filter(user => 
          user.requestedRole === "teacher" || 
          user.role === "teacher" ||
          user.teacherId // If they have a teacherId, they're probably a teacher
        )
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      
      setPendingUsers(finalUsers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      setMessage('Error fetching pending users: ' + error.message);
      setLoading(false);
    }
  };

  const approveUser = async (userId, userEmail, userData) => {
    setProcessing({ ...processing, [userId]: 'approving' });
    try {
      // For teacher approvals (covers all cases: requestedRole=teacher, role=teacher, or orphaned pending users)
      if (userData.requestedRole === ROLES.TEACHER || userData.role === ROLES.TEACHER || 
          (userData.status === 'pending' && userData.role === null && !userData.requestedRole)) {
        await updateDoc(doc(db, "users", userId), {
          role: "teacher",
          requestedRole: deleteField(),
          status: "active",
          isActive: true,
          plan: "trial",
          subscriptionStatus: "active",
          studentsLimit: 5,
          trialEndsAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days from now
          createdAt: serverTimestamp(),
        });
        setMessage(`✅ User ${userEmail} approved as teacher with 7-day trial (5 students max)!`);
      } else if (userData.requestedRole) {
        // For students or other specific roles
        await updateDoc(doc(db, 'users', userId), {
          role: userData.requestedRole,
          requestedRole: deleteField(),
          status: STATUSES.ACTIVE,
          isActive: true,
          approvedAt: serverTimestamp(),
        });
        setMessage(`✅ User ${userEmail} approved successfully as ${userData.requestedRole}!`);
      } else {
        // Fallback: assume they want to be a teacher if unclear
        await updateDoc(doc(db, "users", userId), {
          role: "teacher",
          requestedRole: deleteField(),
          status: "active",
          isActive: true,
          plan: "trial",
          subscriptionStatus: "active",
          studentsLimit: 5,
          trialEndsAt: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days from now
          createdAt: serverTimestamp(),
        });
        setMessage(`✅ User ${userEmail} approved as teacher (default) with 7-day trial!`);
      }
      
      // Refresh the pending users list
      setupPendingUsersListener();
    } catch (error) {
      setMessage(`❌ Error approving user: ${error.message}`);
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const rejectUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to reject ${userEmail}? This will disable their account.`)) {
      return;
    }

    setProcessing({ ...processing, [userId]: 'rejecting' });
    try {
      await updateDoc(doc(db, 'users', userId), {
        status: STATUSES.DISABLED,
        isActive: false,
        rejectedAt: new Date(),
      });
      
      setMessage(`🚫 User ${userEmail} rejected and disabled.`);
      
      // Refresh the pending users list
      setupPendingUsersListener();
    } catch (error) {
      setMessage(`❌ Error rejecting user: ${error.message}`);
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case ROLES.TEACHER: return '👩‍🏫';
      case ROLES.STUDENT: return '🎓';
      case ROLES.MASTER: return '👑';
      default: return '👤';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case ROLES.TEACHER: return 'bg-blue-100 text-blue-800';
      case ROLES.STUDENT: return 'bg-blue-100 text-blue-800';
      case ROLES.MASTER: return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Update teacher subscription
  const updateSubscription = async () => {
    if (!selectedTeacher) return;
    
    try {
      const now = new Date();
      const periodDays = newSubscription.plan === 'yearly' ? 365 : 30;
      const periodEndsAt = new Date(now.getTime() + (periodDays * 24 * 60 * 60 * 1000));
      
      const subscriptionData = {
        subscription: {
          plan: newSubscription.plan,
          status: 'active',
          startedAt: serverTimestamp(),
          periodEndsAt: Timestamp.fromDate(periodEndsAt)
        },
        studentLimit: newSubscription.studentLimit
      };
      
      await updateDoc(doc(db, 'users', selectedTeacher.id), subscriptionData);
      
      setMessage(`✅ Subscription updated for ${selectedTeacher.email}: ${newSubscription.plan} plan with ${newSubscription.studentLimit} students`);
      setShowSubscriptionModal(false);
      setSelectedTeacher(null);
    } catch (error) {
      console.error('Error updating subscription:', error);
      setMessage(`❌ Error updating subscription: ${error.message}`);
    }
  };

  const openSubscriptionModal = (teacher) => {
    setSelectedTeacher(teacher);
    setNewSubscription({ 
      plan: teacher.subscription?.plan || 'monthly',
      studentLimit: teacher.studentLimit || 30
    });
    setShowSubscriptionModal(true);
  };

  const handleMigratePendingTeachers = async () => {
    if (!window.confirm('This will migrate old-shape pending teachers (role=teacher) to new-shape (requestedRole=teacher). Continue?')) {
      return;
    }

    setMigrationLoading(true);
    try {
      const result = await migratePendingTeachers();
      setMessage(`✅ Migration completed! ${result.migrated}/${result.total} users migrated successfully.`);
      
      // Refresh the pending users list to show updated data
      setupPendingUsersListener();
    } catch (error) {
      setMessage(`❌ Migration failed: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  const handleNormalizeLegacyStudents = async () => {
    if (!window.confirm('Normalize legacy student documents? This will update role/teacherId/status fields.')) return;
    try {
      const result = await normalizeLegacyStudents();
      setMessage(`✅ Normalized ${result.updated} legacy students out of ${result.total} checked.`);
    } catch (error) {
      setMessage(`❌ Migration failed: ${error.message}`);
    }
  };

  const handleDryRunMigration = async () => {
    setMigrationLoading(true);
    try {
      const result = await dryRunPendingTeachersMigration();
      setMessage(`🧪 Dry run completed: Found ${result.wouldMigrate} old-shape pending teachers that would be migrated.`);
    } catch (error) {
      setMessage(`❌ Dry run failed: ${error.message}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  if (!userDoc || userDoc.role !== ROLES.MASTER) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This page is only accessible to master administrators.</p>
          <Link to="/" className="text-blue-600 hover:underline mt-4 block">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading pending users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white p-6">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">🔐 User Approval Center</h1>
              <p className="text-blue-200">Review and approve pending user registrations</p>
            </div>
            <Link 
              to="/master-admin" 
              className="bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-lg transition duration-200"
            >
              ← Back to Admin
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.includes('✅') 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : message.includes('❌') || message.includes('🚫')
                ? 'bg-red-100 text-red-800 border border-red-200'
                : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
            {message}
          </div>
        )}

        {/* Stats */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">📊 Pending Approvals</h2>
              <p className="text-gray-600">Users waiting for account activation</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">{pendingUsers.length}</div>
                <div className="text-gray-600">Pending</div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleDryRunMigration}
                  disabled={migrationLoading}
                  className="px-3 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded text-sm font-medium transition duration-200 disabled:opacity-50"
                >
                  {migrationLoading ? '...' : '🧪 Dry Run Migration'}
                </button>
                <button
                  onClick={handleMigratePendingTeachers}
                  disabled={migrationLoading}
                  className="px-3 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-sm font-medium transition duration-200 disabled:opacity-50"
                >
                  {migrationLoading ? '...' : '🔧 Migrate Old Teachers'}
                </button>
                <button
                  onClick={handleNormalizeLegacyStudents}
                  className="px-3 py-1 bg-green-100 text-green-800 hover:bg-green-200 rounded text-sm font-medium"
                >
                  🔧 Normalize Legacy Students
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Users List */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800">⏳ Pending User Registrations</h2>
          </div>
          
          <div className="p-6">
            {pendingUsers.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">All Clear!</h3>
                <p className="text-gray-500">
                  No pending user registrations at this time.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4">User</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Role</th>
                      <th className="text-left py-3 px-4">ID</th>
                      <th className="text-left py-3 px-4">Registered</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">{getRoleIcon(user.role)}</span>
                            <div>
                              <div className="font-medium text-gray-900">
                                {user.displayName || `${user.firstName} ${user.lastName}` || 'No name'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm text-gray-900 font-medium">{user.email}</div>
                          {user.emailValidation && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                user.emailValidation.confidence === 'high' ? 'bg-green-100 text-green-800' :
                                user.emailValidation.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {user.emailValidation.confidence === 'high' ? '✅' :
                                 user.emailValidation.confidence === 'medium' ? '🔍' : '❓'}
                                {user.emailValidation.confidence} confidence
                              </span>
                              <span className="text-xs text-gray-500" title={user.emailValidation.reason}>
                                {user.emailValidation.domain}
                              </span>
                            </div>
                          )}
                          {user.pendingReason && (
                            <div className="mt-1 text-xs text-amber-600 font-medium">
                              📋 {user.pendingReason}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.requestedRole || user.role || 'teacher')}`}>
                            {user.requestedRole || user.role || 'teacher (assumed)'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {user.teacherId || user.studentId || 'N/A'}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => approveUser(user.id, user.email, user)}
                              disabled={processing[user.id]}
                              className="px-3 py-1 bg-green-100 text-green-800 hover:bg-green-200 rounded text-sm font-medium transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processing[user.id] === 'approving' ? '...' : '✅ Approve'}
                            </button>
                            <button
                              onClick={() => rejectUser(user.id, user.email)}
                              disabled={processing[user.id]}
                              className="px-3 py-1 bg-red-100 text-red-800 hover:bg-red-200 rounded text-sm font-medium transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processing[user.id] === 'rejecting' ? '...' : '🚫 Reject'}
                            </button>
                            {(user.requestedRole === ROLES.TEACHER || user.role === ROLES.TEACHER) && (
                              <button
                                onClick={() => openSubscriptionModal(user)}
                                className="px-3 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-sm font-medium transition duration-200"
                              >
                                💳 Subscription
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">📋 Instructions</h3>
          <ul className="text-blue-800 space-y-2">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">✅</span>
              <span><strong>Approve:</strong> Activates the user account and grants access to the platform</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 mr-2">🚫</span>
              <span><strong>Reject:</strong> Disables the user account permanently</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">ℹ️</span>
              <span><strong>Note:</strong> All approved users will be able to log in immediately</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Subscription Management Modal */}
      {showSubscriptionModal && selectedTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">💳 Manage Subscription</h3>
            <p className="text-gray-600 mb-4">
              Update subscription for: <strong>{selectedTeacher.email}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Subscription Plan</label>
                <select
                  value={newSubscription.plan}
                  onChange={(e) => setNewSubscription({...newSubscription, plan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="trial">Trial (7 days, 5 students)</option>
                  <option value="monthly">Monthly ($29/month)</option>
                  <option value="yearly">Yearly ($290/year)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Student Limit</label>
                <select
                  value={newSubscription.studentLimit}
                  onChange={(e) => setNewSubscription({...newSubscription, studentLimit: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="5">5 students (Trial)</option>
                  <option value="30">30 students (Monthly/Yearly)</option>
                  <option value="50">50 students (Yearly Plus)</option>
                  <option value="100">100 students (Enterprise)</option>
                </select>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> This will {newSubscription.plan === 'trial' ? 'reset their trial period' : `activate their ${newSubscription.plan} subscription`}.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={updateSubscription}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium transition duration-200"
              >
                💾 Update Subscription
              </button>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 font-medium transition duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}